import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { lessonService } from '../../api/lessonService';
import { useAuth } from '../../context/AuthContext';
import { LessonContent, PublicLessonRef } from '../../types/lesson';
import LessonPlayer from './LessonPlayer';
import '../../styles/publicCollection.css';
import '../../styles/lesson.css';
import '../../styles/publicLesson.css';
import logo from '../../assets/logo.png';
import Footer from '../common/footer';
import { PUBLIC_APP_ORIGIN } from '../../config/publicUrls';
import { useShareData } from '../../hooks/useShareData';
import ShareHead, { ShareNotFoundHead } from '../common/ShareHead';
import { lessonPageText } from '../../utils/shareText';

// The result of fetching a lesson's CDN snapshot, tagged with the ref it was fetched for. (In
// local/dev without LESSON_CDN_BASE_URL the share response carries the content inline instead.)
interface ContentState {
    ref: PublicLessonRef | null;
    content: LessonContent | null;
    failed: boolean;
}

const PublicLessonPage: React.FC = () => {
    const { t } = useTranslation('lesson');
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const authContext = useAuth();
    const isLoggedIn = !!authContext?.user;

    // Two-step fetch per the S3+CloudFront design: a cheap API call resolves metadata + a cdn_url,
    // then the actual (potentially large) lesson content is fetched straight from the CDN — never
    // round-tripped through the Go API/Postgres for anonymous readers. The API call goes through
    // lessonService (shared client: timeout, error handling); on a server-rendered first load the
    // metadata is already in the page, so the page renders at once (utils/ssrPage.ts).
    const { data: ref, loading, failed } = useShareData<PublicLessonRef>(`/l/${token}`, () =>
        lessonService.getPublicByToken(encodeURIComponent(token ?? '')));
    const [body, setBody] = useState<ContentState>({ ref: null, content: null, failed: false });

    useEffect(() => {
        // Inline content needs no fetch, and a ref with neither has nothing to fetch (it's
        // already an error at render time, below).
        if (!ref || ref.content || !ref.cdn_url) return;
        // A plain fetch — it's a CloudFront URL, not this API.
        let cancelled = false;
        fetch(ref.cdn_url)
            .then((res) => {
                if (!res.ok) throw new Error('cdn fetch failed');
                return res.json();
            })
            .then((snapshot: { content: LessonContent }) => {
                if (!cancelled) setBody({ ref, content: snapshot.content, failed: false });
            })
            .catch(() => {
                if (!cancelled) setBody({ ref, content: null, failed: true });
            });
        return () => { cancelled = true; };
    }, [ref]);

    // Inline content is usable on the very first render; otherwise wait for this ref's CDN fetch.
    // Worked out during render rather than in the effect, so a lesson with no content at all goes
    // straight to the error screen instead of first rendering the page (and its indexable head).
    const content = ref?.content ?? (body.ref === ref ? body.content : null);
    const contentFailed = !!ref && !ref.content && (!ref.cdn_url || (body.ref === ref && body.failed));

    if (loading) {
        return (
            <div className="pc-container">
                <div className="pc-loading">{t('public.loading', 'Loading lesson…')}</div>
            </div>
        );
    }

    if (failed || !ref || contentFailed) {
        return (
            <div className="pc-container">
                <ShareNotFoundHead />
                <div className="pc-error">
                    <h2>{t('public.unavailable', 'Lesson unavailable')}</h2>
                    <p>{t('public.not_available', 'This lesson is not available.')}</p>
                    <button className="pc-btn" onClick={() => navigate('/')}>{t('public.go_home', 'Go home')}</button>
                </div>
            </div>
        );
    }

    // Same formulas and JSON-LD as weavewords-api's ShareRenderHandler.lessonPage.
    const { title: pageTitle, description: pageDesc } = lessonPageText(ref);
    const lessonUrl = `${PUBLIC_APP_ORIGIN}/l/${token}`;

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        name: ref.title,
        description: pageDesc,
        url: lessonUrl,
        inLanguage: ref.language,
        educationalLevel: ref.language_level,
        learningResourceType: 'interactive exercise',
        timeRequired: `PT${ref.duration_minutes}M`,
        isPartOf: { '@type': 'WebSite', name: 'WeaveWords', url: `${PUBLIC_APP_ORIGIN}/` },
    };

    return (
        <>
            <ShareHead title={pageTitle} description={pageDesc} url={lessonUrl} jsonLd={jsonLd} />

            <div className="pc-container">
                {!isLoggedIn && (
                    <header className="pc-header">
                        <div className="pc-header-inner">
                            <img src={logo} alt="WeaveWords" className="pc-logo" onClick={() => navigate('/')} />
                            <button className="pc-header-cta" onClick={() => navigate('/register')}>
                                {t('public.register_cta', 'Sign up free')}
                            </button>
                        </div>
                    </header>
                )}

                <main className="pl-main">
                    {content
                        ? <LessonPlayer title={ref.title} content={content} language={ref.language} readOnly />
                        : <div className="pc-loading">{t('public.loading', 'Loading lesson…')}</div>}
                </main>

                {!isLoggedIn && (
                    <section className="pc-promo">
                        <h2>{t('public.promo_title', 'Want to create lessons like this?')}</h2>
                        <p>{t('public.promo_body', 'Join WeaveWords to generate your own AI-powered vocabulary lessons.')}</p>
                        <div className="pc-actions">
                            <button className="pc-btn pc-btn-primary" onClick={() => navigate('/register')}>
                                {t('public.register_cta', 'Sign up free')}
                            </button>
                            <button className="pc-btn" onClick={() => navigate('/login')}>
                                {t('public.already_have_account', 'I already have an account')}
                            </button>
                        </div>
                    </section>
                )}

                <Footer />
            </div>
        </>
    );
};

export default PublicLessonPage;
